import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowLeft, ArrowUp, Download, Eye, Loader2, Play, RefreshCw, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/PageHeader";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const Card = ({ title, children, actions, testid }) => (
  <section className="rounded-lg border border-slate-200 bg-white" data-testid={testid}>
    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3"><h2 className="text-sm font-semibold text-slate-900">{title}</h2>{actions}</div>
    <div className="space-y-3 p-4">{children}</div>
  </section>
);

const ModuleRow = ({ m, sel, i, n, onToggle, onMove, onDesc, companyId }) => {
  const [busy, setBusy] = useState(false);
  const ai = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/proposals/ai-description", { company_id: companyId, name: m.name, description: sel?.description || m.description }); onDesc(data.description); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid={`module-row-${m.key}`}>
      <div className="flex items-center gap-2">
        <Checkbox checked={!!sel} onCheckedChange={onToggle} data-testid={`module-toggle-${m.key}`} />
        <span className="font-medium text-slate-900">{m.name}</span>
        {m.new && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800" data-testid={`module-new-${m.key}`}>New</span>}
        <span className="text-xs text-slate-400">{m.route ? `${m.route} · ${m.viewport === "mobile" ? "390 px" : "1440 px"}` : "no screen"}{m.data && ` · data: ${m.data}`}</span>
        {sel && <div className="ml-auto flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(-1)} data-testid={`module-up-${m.key}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === n - 1} onClick={() => onMove(1)} data-testid={`module-down-${m.key}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
        </div>}
      </div>
      {sel && <div className="mt-2 space-y-1.5">
        <Textarea rows={3} value={sel.description} onChange={(e) => onDesc(e.target.value)} data-testid={`module-desc-${m.key}`} />
        <Button variant="outline" size="sm" className="gap-1.5" onClick={ai} disabled={busy} data-testid={`module-ai-${m.key}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Write with AI
        </Button>
      </div>}
    </div>
  );
};

const Access = ({ doc, set, people }) => {
  const a = doc.access;
  const tog = (k, v) => set({ ...doc, access: { ...a, [k]: a[k].includes(v) ? a[k].filter((x) => x !== v) : [...a[k], v] } });
  const [q, setQ] = useState("");
  const users = people.users.filter((u) => `${u.name} ${u.company}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card title="Access" testid="proposal-access">
      <div className="flex flex-wrap gap-4">{people.roles.map((r) => (
        <label key={r.key} className="flex items-center gap-2 text-sm"><Checkbox checked={a.roles.includes(r.key)} onCheckedChange={() => tog("roles", r.key)} data-testid={`access-role-${r.key}`} />{r.label}</label>
      ))}</div>
      <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={a.scope === "all"} onCheckedChange={(c) => set({ ...doc, access: { ...a, scope: c ? "all" : "company" } })} data-testid="access-scope-all" />Roles apply to every company (otherwise only {doc.company})</label>
      <Input placeholder="Search people" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="access-user-search" />
      <div className="max-h-56 space-y-1 overflow-y-auto">{users.map((u) => (
        <label key={u.id} className="flex items-center gap-2 text-sm"><Checkbox checked={a.users.includes(u.id)} onCheckedChange={() => tog("users", u.id)} data-testid={`access-user-${u.id}`} />{u.name}<span className="text-xs text-slate-400">{u.role_label} · {u.company}</span></label>
      ))}</div>
    </Card>
  );
};

const openPdf = async (id, v) => {
  const r = await api.get(`/admin/proposals/${id}/file`, { params: { v }, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  Object.assign(document.createElement("a"), { href: url, download: `proposal_v${v}.pdf` }).click();
};

export default function ProposalBuilderPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [reg, setReg] = useState([]);
  const [people, setPeople] = useState({ roles: [], users: [] });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const poll = useRef();
  const load = useCallback(() => api.get(`/admin/proposals/${id}`).then((r) => { setDoc((d) => (d && r.data.job.state !== "running" && d._dirty ? { ...d, job: r.data.job, versions: r.data.versions } : r.data)); return r.data; }), [id]);
  const refresh = (manual) => api.get("/admin/proposals/modules").then((r) => { setReg(r.data.modules); manual && toast.success(`${r.data.modules.filter((m) => m.new).length} new module(s) found`); });
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); refresh(); api.get("/admin/proposals/people").then((r) => setPeople(r.data)); return () => clearInterval(poll.current); }, [load]);
  useEffect(() => {
    clearInterval(poll.current);
    if (doc?.job.state === "running") poll.current = setInterval(() => load().then((d) => d.job.state !== "running" && (d.job.state === "done" ? toast.success("New version ready") : toast.error(d.job.error || "Generation failed"))), 2000);
  }, [doc?.job.state, load]);
  if (!doc) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  const set = (d) => setDoc({ ...d, _dirty: true });
  const selIdx = (k) => doc.modules.findIndex((m) => m.key === k);
  const ordered = [...doc.modules.map((s) => reg.find((m) => m.key === s.key) || { ...s, route: null }), ...reg.filter((m) => selIdx(m.key) < 0)];
  const toggle = (m) => set({ ...doc, modules: selIdx(m.key) >= 0 ? doc.modules.filter((x) => x.key !== m.key) : [...doc.modules, { key: m.key, name: m.name, description: m.description }] });
  const move = (k, d) => { const a = [...doc.modules]; const i = selIdx(k); [a[i], a[i + d]] = [a[i + d], a[i]]; set({ ...doc, modules: a }); };
  const desc = (k, v) => set({ ...doc, modules: doc.modules.map((x) => (x.key === k ? { ...x, description: v } : x)) });
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/admin/proposals/${id}`, { company_id: doc.company_id, title: doc.title, client: doc.client, intro: doc.intro, modules: doc.modules, access: doc.access }); setDoc(data); toast.success("Saved"); return true; }
    catch (e) { toast.error(apiError(e)); return false; } finally { setSaving(false); }
  };
  const generate = async () => { if (!(await save())) return; await api.post(`/admin/proposals/${id}/generate`).then(load).catch((e) => toast.error(apiError(e))); };
  const j = doc.job;

  return (
    <div className="space-y-5" data-testid="proposal-builder">
      <Link to="/admin/proposals" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-proposals"><ArrowLeft className="h-4 w-4" /> Documents</Link>
      <PageHeader eyebrow={doc.company} title={doc.title} subtitle="Screens are captured live from this company with amounts and names blurred."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={save} disabled={saving} className="gap-1.5" data-testid="save-proposal-button"><Save className="h-4 w-4" /> Save</Button>
          <Button onClick={generate} disabled={j.state === "running" || !doc.modules.length} className="gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="generate-proposal-button"><Play className="h-4 w-4" /> Generate PDF</Button>
        </div>} />
      {j.state === "running" && <div className="rounded-lg border border-teal-200 bg-teal-50 p-4" data-testid="proposal-progress">
        <div className="mb-2 flex items-center gap-2 text-sm text-teal-900"><Loader2 className="h-4 w-4 animate-spin" />{j.step} ({j.done}/{j.total})</div>
        <Progress value={(100 * (j.done || 0)) / (j.total || 1)} />
      </div>}
      {j.state !== "running" && j.failures?.length > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="proposal-failures">{j.failures.map((f) => <div key={f}>{f}</div>)}</div>}
      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="space-y-5">
          <Card title="Document" testid="proposal-details">
            <Input value={doc.title} onChange={(e) => set({ ...doc, title: e.target.value })} placeholder="Title" data-testid="proposal-title-input" />
            <Input value={doc.client} onChange={(e) => set({ ...doc, client: e.target.value })} placeholder="Client (optional)" data-testid="proposal-client-input" />
            <Textarea rows={4} value={doc.intro} onChange={(e) => set({ ...doc, intro: e.target.value })} placeholder="Introduction" data-testid="proposal-intro-input" />
          </Card>
          <Card title={`Modules (${doc.modules.length} selected)`} testid="proposal-modules"
            actions={<Button variant="outline" size="sm" className="gap-1.5" onClick={() => refresh(true)} data-testid="refresh-modules-button"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>}>
            {ordered.map((m) => { const i = selIdx(m.key); return <ModuleRow key={m.key} m={m} sel={doc.modules[i]} i={i} n={doc.modules.length} companyId={doc.company_id} onToggle={() => toggle(m)} onMove={(d) => move(m.key, d)} onDesc={(v) => desc(m.key, v)} />; })}
          </Card>
        </div>
        <div className="space-y-5">
          <Access doc={doc} set={set} people={people} />
          <Card title="Versions" testid="proposal-versions">
            {!doc.versions.length && <p className="text-sm text-slate-500">Not generated yet</p>}
            {[...doc.versions].reverse().map((v) => (
              <div key={v.v} className="flex items-center gap-2 text-sm" data-testid={`proposal-version-${v.v}`}>
                <span className="font-semibold">v{v.v}</span><span className="flex-1 text-xs text-slate-500">{fmtDateTime(v.created_at)} · {v.created_by}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/documents/${id}?v=${v.v}`)} data-testid={`view-version-${v.v}`}><Eye className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openPdf(id, v.v)} data-testid={`download-version-${v.v}`}><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
